import React, { Component, useCallback, useEffect, useRef, useState } from 'react'
import { array } from 'prop-types'
import { getDataIndex, Sorters, getFilterTitle, CellRenderers, ColumnFilterTypes } from 'utils/tableUtils'
import { withRouter } from 'react-router-dom'
import { Button, Divider, Dropdown, Typography, Menu, Tooltip, Input } from 'antd'
import _, { compact, filter, find, intersection, isEmpty, map, omit, pick, pickBy } from 'lodash'
import classnames from 'classnames'
import { AngleDownIcon, CircleCrossIcon } from './Icons'
import { getTextValue } from './ColumnFilter';
import ColumnFilterComparison from './ColumnFilterComparison'
import { getVocabulary, getInnerText } from './Vocabulary'
import { parseURLSearch, updateURLSearch } from 'utils/urlUtil'

class FilterBar extends Component {
    newMountKey = undefined;
    moreColumnSearchRef = React.createRef();
    static defaultProps = {
        columns: [],
        prefixText: "Filters:",
        btnSize: 'middle',
        btnType: 'default',
        displayType: 'horizontal',
        currentActiveBlankFilter: undefined
    }
    static propTypes = {
        columns: array
    }
    static getDerivedStateFromProps(props, state){
        const { onClearFilter, onChange } = props;
        const filters = onChange ? (props.filters || {}) : parseURLSearch(props.location.search).filters;
        const { activeFilterKeys, alwaysActiveFilters, currentActiveBlankFilter } = state;
        const filterKeysFromProps = Object.keys(filters);
        const newFilterKeys = _.difference(filterKeysFromProps, activeFilterKeys);

        if(newFilterKeys.length){
            activeFilterKeys.push.apply(activeFilterKeys, newFilterKeys);

            return {
                activeFilterKeys,
                filters
            };
        }

        // If no onClearFilter function, that means clear filters is handled outside of this component, i.e. Product/Subproduct listing pages
        // Derive removed filters for such cases.
        const removedFilterKeys = _.difference(_.difference(activeFilterKeys, filterKeysFromProps), alwaysActiveFilters);
        if (!_.isFunction(onClearFilter) && removedFilterKeys.length && !currentActiveBlankFilter) {
            const newActiveFilterKeys = [...activeFilterKeys];
            removedFilterKeys.forEach(key => {
                const index = newActiveFilterKeys.indexOf(key);
                if(index >= 0){
                    newActiveFilterKeys.splice(index, 1);
                }
            });
            
            return {
                activeFilterKeys: newActiveFilterKeys,
                filters
            };
        }

        return {
            filters
        };
    }
    constructor(props){
        super(props);

        const alwaysActiveFilters = props.columns.filter(c => c.filterIndex).sort(Sorters.NUMBER("filterIndex")).map(getDataIndex);

        this.state = {
            activeFilterKeys: [...alwaysActiveFilters],
            alwaysActiveFilters,
            searchValue: ''
        }
        _.bindAll(
            this,
            "renderSingleFilter",
            "applyFilter",
            "addFilter",
            "onClearFilter",
            "applyAllFilters",
        )
    }
    renderSingleFilter(key, index, array){
        const { columns, btnSize, btnType } = this.props;
        const { filters } = this.state;
        const column = _.find(columns, c => {
            const match = getDataIndex(c) === key;
            if(match){
                return true;
            }
            if(c.items?.length){
                return find(c.items, { value: key });
            }
            return false;
        });
        if(!column){
            return null;
        }
        return (
            <React.Fragment key={key}>
                <FilterDropDown btnSize={btnSize} btnType={btnType} column={column} filteredValue={filters[key]} newMountKey={this.newMountKey} apply={this.applyFilter} resetNewMountKey={() => this.newMountKey = undefined} isLastChild={index === array.length - 1} allFilters={filters} applyAllFilters={this.applyAllFilters} />
            </React.Fragment>
        )
    }
    applyFilter(dataIndex, values, deleteEmpty = true){
        const { onChange } = this.props;
        let pagination, filters, query;
        if(onChange){
            filters = _.clone(this.props.filters || {});
        } else {
            const parsed = parseURLSearch(this.props.location.search);
            pagination = parsed.pagination;
            query = parsed.query;
            if(deleteEmpty) {
                pagination.page = 0
            }
            filters = parsed.filters;
        }
        if(_.isEmpty(values) && deleteEmpty){
            delete filters[dataIndex];
            if (!this.state.alwaysActiveFilters.includes(dataIndex)) {
                this.setState({
                    activeFilterKeys: _.difference(this.state.activeFilterKeys, [dataIndex]),
                    currentActiveBlankFilter: undefined
                });
            }
        } else {
            this.setState({
                activeFilterKeys: _.uniq([...this.state.activeFilterKeys, dataIndex]),
                currentActiveBlankFilter: _.isEmpty(values) ? dataIndex : undefined
            });
        }
        // only do setState(see above) and do not pass filter data to parent if values is empty, i.e no filter value is yet selected
        if(!_.isEmpty(values)) {
            filters[dataIndex] = values;
        }
        if(onChange){
            onChange(filters);
        } else {
            updateURLSearch(this.props.history, { pagination, filters, query });
        }
    }
    applyAllFilters(filters){
        const { onChange } = this.props;
        if(onChange){
            onChange(filters);
        } else {
            // update URL search not implemented as not needed at present
        }
    }
    onClearFilter() {
        const { onClearFilter } = this.props;
        const { alwaysActiveFilters } = this.state;

        this.setState({
            activeFilterKeys: [...alwaysActiveFilters],
        })

        onClearFilter();
    }
    addFilter({key}){
        this.applyFilter(key, [], false);
        this.newMountKey = key;
        this.setState({searchValue: ''});
    }
    filterColumns = columns => {
        const { searchValue } = this.state;
        let charLength = 0;
        const filterColumns = columns.filter(column => {
            const title = getFieldTitle(column);
            if (title?.length > charLength) {
                charLength = title.length;
            }
            return searchValue ?  getInnerText(column.title).toLowerCase().includes(searchValue.toLowerCase()) : column;
        })
        return {
            filterColumns,
            charLength
        }
    }
    onVisibleChange = visible => {
        const { searchValue } = this.state;
        if (!visible && searchValue) {
            this.setState({searchValue: ''}, () => {
            });
        }
        
        // SetTimeout, because the first time dropdown opens, ref is null
        setTimeout(() => {
            if (this.moreColumnSearchRef.current) {
                this.moreColumnSearchRef.current.focus();
            }
        }, 200);
    }
    renderMoreButton(vacantFilterColumns){
        if(vacantFilterColumns.length === 0){
            return null;
        }
        const { btnSize, displayType } = this.props;
        const { searchValue } = this.state;
        const { filterColumns , charLength} = this.filterColumns(vacantFilterColumns);
        return (
            <Dropdown
                trigger="click"
                overlayStyle={{ maxHeight: 300, overflow: "auto" }}
                overlayClassName="column-filter"
                onVisibleChange={this.onVisibleChange}
                overlay={
                    <Menu onClick={this.addFilter}>
                        <div className='p-xs p-b-0'>
                            <Input
                                ref={this.moreColumnSearchRef}
                                style={{width: displayType === 'vertical' ? '100%' : `${charLength}`}}
                                placeholder="Search"
                                value={searchValue}
                                onChange={e => this.setState({searchValue: e.target.value})}
                            />
                            { filterColumns.length === 0 && <div className='m-t-sm text-center'>Not Found</div> }
                        </div>
                        {
                            filterColumns.map(c => <Menu.Item key={getDataIndex(c)}>{getFilterTitle(c)}</Menu.Item>)
                        }
                    </Menu>
                }
            >
                <Button id='plus-more' size={btnSize} type="primary" className={classnames("text-bold more-button",{"disabled": _.isEmpty(vacantFilterColumns)})}>
                    + More
                </Button>
            </Dropdown>
        )
    }
    render() {
        const { columns, prefixText, className, displayType, marginTop = -8, onClearFilter, extra } = this.props;
        const { activeFilterKeys } = this.state;
        const appliedFilterColumns = [], vacantFilterColumns = [];
        const activeColumnFilterKeys = [];
        columns.forEach(column => {
            if(_.isFunction(column.filterDropdown)){
                const dataIndex = getDataIndex(column);
                const itemsKeys = map(column.items, "value");
                if(activeFilterKeys.includes(dataIndex) || intersection(activeFilterKeys, itemsKeys).length){
                    // ENG-32917 - activeColumnFilterKeys used to render filter for only columns that have fiter dropdown
                    activeColumnFilterKeys.push(dataIndex)
                    appliedFilterColumns.push(column);
                } else {
                    vacantFilterColumns.push(column);
                }
            }
        });

        const groupedFilters = filter(columns, c => c.items?.length);
        const groupedColumnKeys = map(groupedFilters, getDataIndex);
        
        return (
            <div className={classnames("d-flex p-xs filter-bar", className, displayType)} style={{marginTop}}>
                {
                    prefixText
                    ?
                    <div className="filter-bar-prefix">
                        <Typography.Text strong className="m-r-sm">{prefixText}</Typography.Text>
                    </div>
                    :
                    null
                }
                <div className={"filters-container"}>
                {
                    map(activeColumnFilterKeys, (key, index, array) => {
                        if(activeFilterKeys.includes(key) || groupedColumnKeys.includes(key)){
                            return this.renderSingleFilter(key, index, array);
                        }
                        return null;
                    })
                }
                {
                    this.renderMoreButton(vacantFilterColumns)
                }
                {
                    _.isFunction(onClearFilter)
                    ?
                    <Button id='clear-filters' className='m-l-xs' type='text' onClick={this.onClearFilter}>Clear filters</Button>
                    :
                    null
                }
                {extra}
                </div>
            </div>
        )
    }
}

export default withRouter(FilterBar);

export {
    FilterBar as FilterBarBase
}

function FilterDropDown({ column, btnSize = 'medium', btnType = 'default', filteredValue, newMountKey, apply, resetNewMountKey, allFilters, applyAllFilters }) {
    const dataIndex = getDataIndex(column);

    /**
     * Using stringify here instead of toString, because [null] (for null filter value) return "" (empty string)
     */
    const filteredValueString = JSON.stringify(filteredValue || []);
    const [visible, setVisible] = useState(() => dataIndex === newMountKey);
    if(dataIndex === newMountKey) {
        resetNewMountKey();
    }

    const selectedKeys = useRef();
    const setSelectedKeys = useCallback((values) => {
        selectedKeys.current = values;
    }, []);
    
    
    useEffect(() => {
        /**
         * Checking less than 3 because empty array will have "[]" (min length 2)
         */
        const isFilterEmpty = filteredValueString.length < 3;
        if(_.isEmpty(selectedKeys.current) && isFilterEmpty && !visible && _.isUndefined(column.filterIndex)){
            apply(dataIndex,[])
        }   
    },[visible, apply, dataIndex, filteredValueString, column.filterIndex])
    
    const confirm = useCallback(() => {
        setVisible(false);
        apply(dataIndex, selectedKeys.current);
    }, [apply, dataIndex]);
    
    const clearFilters = useCallback((e) => {
        if( _.isFunction(e?.stopPropagation)){
            e.stopPropagation()
        }
        setVisible(false);
        if(column.items?.length){
            applyAllFilters(omit(allFilters, map(column.items, "value")));
        } else {
            apply(dataIndex, []);
        }
    }, [apply, dataIndex, column, allFilters, applyAllFilters]);

    const properties = {
        selectedKeys: filteredValue || [],
        setSelectedKeys,
        confirm,
        clearFilters,
        visible,
        ...pick(column, ["items", "title", "filterOptionMap", "filterTypeMap"]),
        onFilterChange: applyAllFilters,
        filterInfo: allFilters
    }
    if(column.filterLabelFormatter){
        properties.filterLabelFormatter = column.filterLabelFormatter;
    }
    const isGroupFilter = column.items?.length;
    const hasValues =
      properties.selectedKeys.length > 0 ||
      (isGroupFilter && !isEmpty(pick(allFilters, map(column.items, "value"))));
    return (
        <Dropdown 
            onVisibleChange={setVisible} 
            visible={visible} 
            key={dataIndex} 
            overlay={column.filterDropdown(properties)} 
            trigger="click"
        >
                <Button
                    id={'filter-button-' + column.dataIndex}
                    size={btnSize}
                    type={btnType}
                    className={classnames("p-x-sm filter-bar-dropdown-button m-t-xs", { "has-values": hasValues })}
                >
                    <div className='d-flex align-items-center'>
                        <div className='filter-values'>
                            {getFilterTitle(column)}
                            {
                                hasValues
                                ?
                                (
                                    column.allFilterFormatter
                                    ?
                                    <> : {column.allFilterFormatter(properties.selectedKeys, column)}</>
                                    :
                                    column.filterType === ColumnFilterTypes.COMPARISON
                                    ?
                                    <> : {ColumnFilterComparison.formatter(column.receiveInterceptor(properties.selectedKeys), column.filterCompareLabel)}</>
                                    :
                                    isGroupFilter
                                    ?
                                    <> : {renderGroupFilterValues(column, allFilters)}</>
                                    :
                                    properties.selectedKeys.map((key, i) => {
                                        return (
                                            <React.Fragment key={key + '_' + i}>
                                                {i === 0 ? " : " : ", "}
                                                {getFilterLabel(column, key)}
                                            </React.Fragment>
                                        )
                                    })
                                )
                                :
                                null
                            }
                        </div>
                        <AngleDownIcon className="m-l-xs angle-down-svg" />
                        {
                            hasValues
                            &&
                            <>
                                <Divider type="vertical" />
                                <Tooltip title="Clear">
                                    <CircleCrossIcon onClick={clearFilters} />
                                </Tooltip>
                            </>
                        }
                    </div>
                </Button>
        </Dropdown>
    )
}

function renderGroupFilterValues(column, filters){
    const itemKeys = map(column.items, "value");
    const appliedFilters = pickBy(filters, (value, key) => {
        return itemKeys.includes(key);
    });
    return compact(map(appliedFilters, (values, key) => {
        const item = find(column.items, { value: key });
        const options = column.filterOptionMap?.[key];
        return `${item?.label??key}: ${map(values, value => {
            const valueItem = find(options, (o) => o?.id ?? o?.value ?? o === value)
            return (
             valueItem?.label || valueItem?.name || value
            );
        })}`
    })).join(" - ");
}

function getFilterLabel(column, filterValue){
    if(filterValue === column.nullFilterValue){
        if (column.nullFilterLabel) {
            return column.nullFilterLabel;
        }
        return <span className='text-bold'>N/A</span>;
    }
    const filterObject = _.find(column.filters, (object) => getTextValue(object).value === filterValue);
    let label = filterObject ? getTextValue(filterObject).text : filterValue;
    if(column.isUIDateRangeFilter){
        const dates = filterValue.split("-");
        if(dates.length === 1){
            label = dates[0];
        } else {
            const date1 = CellRenderers.DATE(dates[0] * 1);
            const date2 = CellRenderers.DATE(dates[1] * 1);
            label = (<>{date1}-{date2}</>);
        }
    }
    return <span className='text-bold'>{column.filterLabelFormatter ?  column.filterLabelFormatter(label) : label}</span>
}

function getFieldTitle (field){
    if (typeof field.title === "string") {
        return field.title;
    }
    return getVocabulary(field.key) || field.filterTitle || getDataIndex(field);
}
